import React, { useRef, useState, Suspense, useCallback } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import {
  useGLTF,
  useCursor,
  Html,
  Environment,
  Center,
  PresentationControls,
  AccumulativeShadows,
  RandomizedLight,
} from "@react-three/drei";
import { Select } from "@react-three/postprocessing";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import { extend } from "@react-three/fiber";
import { geometry } from "maath";
extend(geometry);
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@nextui-org/react";
import Placeholder from "../Placeholder";
import useCompileReady from "../useCompileReady";
import { useProjectFocus } from "@/stores/projectFocusStore";
import { computeFocusFrame } from "../focusUtils";
import useFocusOcclusionHidden from "../useFocusOcclusionHidden";

// Simple debounce utility
function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

// Presentation model component for the modal canvas
function PresentationModel({ modelPath }) {
  const { scene } = useGLTF(modelPath);
  
  // Clone the scene and enable castShadow on all meshes
  const clonedScene = scene.clone();
  clonedScene.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
  
  return (
    <primitive 
      object={clonedScene} 
    />
  );
}

// Inner component that actually loads and renders the model
// This suspends, and is wrapped by Suspense in the parent component
function ProductModelInner({
  projectName,
  modelPath,
  // Focus-view azimuth for this model, radians around the vertical axis
  // (negative = clockwise seen from above). 0 = straight front view.
  focusYaw = 0,
  // Multiplier on the auto-fit focus zoom. >1 zooms in closer; use to fix
  // models whose bounding box is inflated by stray geometry in the GLB.
  focusZoom = 1,
  ...props
}) {
  const { scene } = useGLTF(modelPath);
  const ref = useRef();
  const groupRef = useRef();
  const { size } = useThree();
  // castShadow on the primitive root does not cascade; enable it on the
  // actual meshes so the accumulated ground shadows include this model.
  React.useMemo(() => {
    scene.traverse((child) => {
      if (child.isMesh) child.castShadow = true;
    });
  }, [scene]);
  // Stay hidden (placeholder up) until shaders are compiled off-thread, so
  // the first draw never stutters the visible scene.
  const compiled = useCompileReady(scene);

  // Hover state
  const [hovered, hover] = useState(null);
  // Debounce hover a bit to stop the ticker from being erratic
  const debouncedHover = useCallback(debounce(hover, 30), []);
  useCursor(hovered);

  // Modal state
  const [isOpen, setIsOpen] = useState(false);

  // Rotate animation
  useFrame((state, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.15; // Smooth rotation speed
    }
  });

  const focusId = `product:${projectName}`;
  // Hide this model while it blocks the view of another focused model.
  const occluded = useFocusOcclusionHidden(focusId, groupRef);

  const openModal = () => {
    setTimeout(() => {
      setIsOpen(true);
    }, 0);
  };

  // First click focuses the camera on the model; clicking it again while
  // focused opens the product preview (same as the card's action button).
  const handleMeshClick = (e) => {
    e.stopPropagation();
    const { focused, setFocus } = useProjectFocus.getState();
    if (focused?.id === focusId) {
      openModal();
      return;
    }
    const frame = computeFocusFrame(groupRef.current, size);
    setFocus({
      id: focusId,
      name: projectName,
      ...frame,
      zoom: frame.zoom * focusZoom,
      yaw: focusYaw,
      actionLabel: "View product",
      onAction: openModal,
    });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      {!compiled && (
        <Placeholder position={props.position || [0, 0.5, 0]} scale={[2, 2, 2]} />
      )}
      <group
        ref={groupRef}
        dispose={null}
        visible={compiled && !occluded}
        {...props}
      >
        <Select enabled={hovered}>
          <primitive
            ref={ref}
            object={scene}
            onPointerOver={() => hover(true)}
            onPointerOut={() => hover(false)}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onClick={handleMeshClick}
            castShadow
            receiveShadow
          >
            <Html>
              {hovered && !isOpen && (
                <div className="content">{projectName}</div>
              )}
            </Html>
          </primitive>
        </Select>
      </group>

      {/* Only render the modal Html when open to avoid event conflicts */}
      {isOpen && (
        <Html portal={{ current: document.body }} zIndexRange={[100, 0]}>
          <Modal
            size="5xl"
            isOpen={isOpen}
            onClose={handleClose}
            backdrop="blur"
            scrollBehavior="inside"
            placement="center"
            isDismissable={true}
            hideCloseButton={false}
            classNames={{
              body: "p-0",
              wrapper: "overflow-hidden",
            }}
          >
            <ModalContent>
              <ModalHeader className="flex flex-col gap-1">
                {projectName}
              </ModalHeader>
              <ModalBody>
                <div style={{ width: "100%", height: "60vh", padding: "0 24px" }}>
                  <Canvas
                    shadows
                    camera={{ position: [0, 0, 8], fov: 35, near: 1, far: 25 }}
                    // style={{ background: "#ffffff" }}
                  >
                    <color attach="background" args={['#f0f0f0']} />
                    <ambientLight intensity={0.8} />
                    <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
                    <Environment preset="dawn" environmentIntensity={1.0} rotation={[0, Math.PI / 4, 0]}/>
                    <PresentationControls
                        global
                        config={{ mass: 1, tension: 250, friction: 25 }}
                        snap={{ mass: 2, tension: 250, friction: 50 }}
                        zoom={2.25}
                        rotation={[0.5, Math.PI / 4, 0]}
                        polar={[-Math.PI / 5, Math.PI / 4]}
                        azimuth={[-Math.PI / 1.75, Math.PI / 4]}>
                      <group position={[0, -1.5, 0]}>
                        <Center top>
                          <Suspense fallback={null}>
                            <PresentationModel modelPath={modelPath} />
                          </Suspense>
                        </Center>
                        <AccumulativeShadows
                          temporal
                          frames={100}
                          alphaTest={0.95}
                          opacity={1}
                          scale={20}
                        >
                          <RandomizedLight
                            amount={8}
                            radius={10}
                            ambient={0.5}
                            position={[0, 10, -2.5]}
                            bias={0.001}
                            size={3}
                          />
                        </AccumulativeShadows>
                      </group>
                    </PresentationControls>
                    <EffectComposer>
                      <Bloom
                        luminanceThreshold={1}
                        intensity={0.85}
                        levels={9}
                        mipmapBlur
                      />
                    </EffectComposer>
                  </Canvas>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onClick={handleClose}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Html>
      )}
    </>
  );
}

// Wrapper component that provides Suspense boundary
// This allows each model to load independently with its own placeholder
export default function ProductModel(props) {
  return (
    <Suspense
      fallback={
        <Placeholder
          position={props.position || [0, 0.5, 0]}
          scale={[2, 2, 2]}
        />
      }
    >
      <ProductModelInner {...props} />
    </Suspense>
  );
}
